import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { Visibility, VisibilityOff, Print, FileDownload } from "@mui/icons-material";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ReportSiblingNav from "../components/Reports/ReportSiblingNav";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import { FooterInternal } from "../components/Footer";
import MultiLineChart, { ChartSeries } from "../components/charts/MultiLineChart";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import {
  evolutionReportService,
  EvolutionResponse,
  ProjectionResponse,
  ProjectionSeries,
} from "../services/reportsService";
import { colors, typography } from "../theme/designSystem";
import { filterFieldSx } from "../theme/filterStyles";
import { maskName } from "../utils/nameMask";
import MonthSelect from "../components/Inputs/MonthSelect";

// Evolutivo de Atendimentos — séries mensais, top 10 profissionais e projeção
// (matemática do módulo 6 do riquinho no backend). Fonte: espelho do Bubble.

const formatCurrency = (value: number): string =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

// rótulo compacto para pontos de moeda (evita poluir o gráfico)
const formatCurrencyCompact = (value: number): string =>
  value >= 1000 ? `${(value / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}k` : String(Math.round(value));

const monthAdd = (month: string, delta: number): string => {
  const [year, monthNumber] = month.split("-").map((v) => parseInt(v, 10));
  const date = new Date(year, monthNumber - 1 + delta, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

const currentMonth = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

// padrão: 1 ano e meio de evolução
const defaultFromMonth = (): string => monthAdd(currentMonth(), -17);

const INDICATORS = [
  { key: "scheduled", label: "Agendados", color: "#03B4C6" },
  { key: "attended", label: "Realizados", color: "#28a745" },
  { key: "cancelledByPatient", label: "Canc. paciente", color: "#dc3545" },
  { key: "cancelledByProfessional", label: "Canc. profissional", color: "#512E5F" },
  { key: "netValue", label: "Faturamento líquido", color: "#f39c12" },
] as const;

type IndicatorKey = (typeof INDICATORS)[number]["key"];

const PROFESSIONAL_COLORS = [
  "#03B4C6", "#28a745", "#dc3545", "#512E5F", "#f39c12",
  "#6610f2", "#e83e8c", "#20c997", "#fd7e14", "#6c757d",
];


const sectionStyle = {
  background: "#fff",
  borderRadius: 2,
  border: "1px solid #e9ecef",
  p: 2,
  mb: 3,
};

const ReportEvolution: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();

  const [fromMonth, setFromMonth] = useState<string>(defaultFromMonth());
  const [toMonth, setToMonth] = useState<string>(currentMonth());
  const [showNames, setShowNames] = useState<boolean>(false);
  const [professionalIndicator, setProfessionalIndicator] = useState<IndicatorKey>("attended");
  const [topSelection, setTopSelection] = useState<string[]>([]);

  const [data, setData] = useState<EvolutionResponse | null>(null);
  const [projection, setProjection] = useState<ProjectionResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const displayName = (name: string): string => (showNames ? name : maskName(name));

  const hasFilterChanges = fromMonth !== defaultFromMonth() || toMonth !== currentMonth();

  const loadData = useCallback(
    async (override?: { from: string; to: string }) => {
      const params = override ?? { from: fromMonth, to: toMonth };
      if (!params.from || !params.to) return;
      setLoading(true);
      setError("");
      try {
        const response = await evolutionReportService.get(params.from, params.to);
        setData(response);
      } catch {
        setError("Não foi possível carregar os dados. Tente novamente.");
      } finally {
        setLoading(false);
      }
    },
    [fromMonth, toMonth]
  );

  const handleFromMonthChange = (value: string) => {
    setFromMonth(value);
    if (value && toMonth) loadData({ from: value, to: toMonth });
  };

  const handleToMonthChange = (value: string) => {
    setToMonth(value);
    if (fromMonth && value) loadData({ from: fromMonth, to: value });
  };

  const clearFilters = () => {
    const from = defaultFromMonth();
    const to = currentMonth();
    setFromMonth(from);
    setToMonth(to);
    loadData({ from, to });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await evolutionReportService.exportXlsx(fromMonth, toMonth);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `evolutivo_${fromMonth}_a_${toMonth}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Não foi possível exportar a planilha. Tente novamente.");
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = () => {
    const wasShowing = showNames;
    setShowNames(false);
    setTimeout(() => {
      window.print();
      setShowNames(wasShowing);
    }, 150);
  };

  useEffect(() => {
    loadData();
    evolutionReportService.getProjection(6).then(setProjection).catch(() => setProjection(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const labels = data?.monthly.map((m) => m.month) ?? [];

  const countSeries: ChartSeries[] = data
    ? INDICATORS.filter((i) => i.key !== "netValue").map((indicator) => ({
        name: indicator.label,
        color: indicator.color,
        values: data.monthly.map((m) => m[indicator.key] as number),
      }))
    : [];

  const revenueSeries: ChartSeries[] = data
    ? [
        {
          name: "Faturamento líquido",
          color: "#f39c12",
          values: data.monthly.map((m) => m.netValue),
        },
      ]
    : [];

  const visibleTopProfessionals = data
    ? data.topProfessionals.filter(
        (p) => topSelection.length === 0 || topSelection.includes(p.professionalExternalId)
      )
    : [];

  const professionalSeries: ChartSeries[] = data
    ? visibleTopProfessionals.map((professional, index) => {
        const byMonth = new Map(professional.series.map((s) => [s.month, s]));
        return {
          name: displayName(professional.professionalName),
          color: PROFESSIONAL_COLORS[index % PROFESSIONAL_COLORS.length],
          values: labels.map((month) => {
            const item = byMonth.get(month);
            return item ? (item[professionalIndicator] as number) : 0;
          }),
        };
      })
    : [];

  const buildProjectionChart = (series: ProjectionSeries, color: string) => {
    const historyMonths = series.history.map((h) => h.month);
    const forecastMonths = series.forecast.map((f) => f.month);
    const chartLabels = [...historyMonths, ...forecastMonths];
    const historyValues = [
      ...series.history.map((h) => h.value),
      ...forecastMonths.map(() => null as number | null),
    ];
    // liga o último ponto do histórico ao primeiro projetado
    const forecastValues: (number | null)[] = [
      ...historyMonths.slice(0, -1).map(() => null as number | null),
      series.history[series.history.length - 1]?.value ?? null,
      ...series.forecast.map((f) => f.value),
    ];
    const bandPad = historyMonths.map(() => null as number | null);
    return {
      labels: chartLabels,
      series: [
        { name: "Histórico", color, values: historyValues },
        { name: "Projeção", color, dashed: true, values: forecastValues },
      ] as ChartSeries[],
      band: {
        color,
        low: [...bandPad, ...series.forecast.map((f) => f.low)],
        high: [...bandPad, ...series.forecast.map((f) => f.high)],
      },
    };
  };

  const isCurrency = professionalIndicator === "netValue";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <style>{`
        @media print {
          .login-header, .login-footer-component, .no-print { display: none !important; }
          body { background: #fff !important; }
          main { margin-top: 0 !important; padding: 0 !important; background: #fff !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          /* tabelas largas: paisagem e sem scroll horizontal, para nada sair cortado */
          @page { size: A4 landscape; margin: 8mm; }
          [style*="overflow-x"], [style*="overflowX"] { overflow: visible !important; }
          table { min-width: 0 !important; width: 100% !important; font-size: 10px !important; }
          thead { display: table-header-group; }
          tr { break-inside: avoid; }
        }
      `}</style>

      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters>
          <Box
            sx={{
              mb: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
              <Typography variant="h4" className="c4u-page-header__title">
                Relatórios / Evolutivo de Atendimentos
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Fonte: agenda (sincronizada de hora em hora). Faturamento líquido = valor realizado −
                descontos. Projeção pela metodologia de sazonalidade do fechamento financeiro.
              </Typography>
            </Box>
            <Box className="no-print">
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
                <MonthSelect
                  label="Mês inicial"
                  value={fromMonth}
                  onChange={handleFromMonthChange}
                  sx={filterFieldSx}
                />
                <MonthSelect
                  label="Mês final"
                  value={toMonth}
                  onChange={handleToMonthChange}
                  sx={filterFieldSx}
                />
              </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges || loading} />
                <Tooltip title={showNames ? "Ocultar nomes" : "Exibir nomes"}>
                  <span>
                    <IconButton
                      className="c4u-btn-icon c4u-btn-icon--outline"
                      onClick={() => setShowNames((v) => !v)}
                      aria-label={showNames ? "Ocultar nomes" : "Exibir nomes"}
                    >
                      {showNames ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Imprimir (nomes sempre mascarados)">
                  <span>
                    <IconButton
                      className="c4u-btn-icon c4u-btn-icon--outline"
                      onClick={handlePrint}
                      disabled={loading || !data}
                      aria-label="Imprimir"
                    >
                      <Print fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Exportar planilha do evolutivo">
                  <span>
                    <IconButton
                      className="c4u-btn-icon c4u-btn-icon--outline"
                      onClick={handleExport}
                      disabled={exporting || loading}
                      aria-label="Exportar planilha"
                    >
                      {exporting ? <CircularProgress size={16} sx={{ color: colors.primary }} /> : <FileDownload fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>

            <ReportSiblingNav current="report-evolution" />

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ ...sectionStyle, textAlign: "center", color: "#dc3545" }}>{error}</Box>
          )}

          {!loading && !error && data && data.monthly.length === 0 && (
            <Box sx={{ ...sectionStyle, textAlign: "center", color: "#6c757d" }}>
              Nenhum atendimento encontrado para o período selecionado.
            </Box>
          )}

          {!loading && !error && data && data.monthly.length > 0 && (
            <>
              {/* Evolução mensal — quantidades */}
              <Box sx={sectionStyle}>
                <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 2 }}>
                  Evolução mensal — agendados, realizados e cancelamentos
                </Typography>
                <MultiLineChart labels={labels} series={countSeries} showValues />
              </Box>

              {/* Evolução mensal — faturamento líquido */}
              <Box sx={sectionStyle}>
                <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 2 }}>
                  Evolução mensal — faturamento líquido
                </Typography>
                <MultiLineChart
                  labels={labels}
                  series={revenueSeries}
                  valueFormatter={formatCurrency}
                  showValues
                  labelFormatter={formatCurrencyCompact}
                />
              </Box>

              {/* Top 10 profissionais */}
              <Box sx={sectionStyle}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1, mb: 2 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold }}>
                    Top 10 profissionais com mais atendimentos
                  </Typography>
                  <Box className="no-print" sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                    <TextField
                      select
                      size="small"
                      label="Profissionais"
                      value={topSelection}
                      onChange={(e) => {
                        const value = e.target.value;
                        setTopSelection(typeof value === "string" ? value.split(",") : value);
                      }}
                      SelectProps={{
                        multiple: true,
                        renderValue: (selected) => {
                          const ids = selected as string[];
                          if (ids.length === 0) return "Todos (top 10)";
                          return `${ids.length} selecionado(s)`;
                        },
                        displayEmpty: true,
                      }}
                      sx={{ ...filterFieldSx, width: 200 }}
                      InputLabelProps={{ shrink: true }}
                    >
                      {data.topProfessionals.map((p) => (
                        <MenuItem key={p.professionalExternalId} value={p.professionalExternalId}>
                          <Checkbox
                            size="small"
                            checked={topSelection.includes(p.professionalExternalId)}
                          />
                          <ListItemText
                            primary={displayName(p.professionalName)}
                            primaryTypographyProps={{ fontSize: 14 }}
                          />
                        </MenuItem>
                      ))}
                    </TextField>
                    {INDICATORS.map((indicator) => (
                      <Chip
                        key={indicator.key}
                        size="small"
                        label={indicator.label}
                        onClick={() => setProfessionalIndicator(indicator.key)}
                        sx={{
                          cursor: "pointer",
                          backgroundColor:
                            professionalIndicator === indicator.key ? indicator.color : "#f1f3f5",
                          color: professionalIndicator === indicator.key ? "#fff" : "#495057",
                          fontWeight: typography.fontWeight.semibold,
                        }}
                      />
                    ))}
                  </Box>
                </Box>
                <MultiLineChart
                  labels={labels}
                  series={professionalSeries}
                  height={340}
                  valueFormatter={isCurrency ? formatCurrency : undefined}
                  showValues
                  labelFormatter={isCurrency ? formatCurrencyCompact : undefined}
                />
              </Box>

              {/* Projeções (6 meses) */}
              {projection && (
                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                  <Box sx={{ ...sectionStyle, flex: "1 1 420px" }}>
                    <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 0.5 }}>
                      Projeção — atendimentos realizados (6 meses)
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "#6c757d", mb: 2 }}>
                      Modelo: {projection.attended.model}
                      {projection.attended.mape != null && ` · erro médio (MAPE) ${projection.attended.mape}%`}
                      {" · banda ~80% de confiança"}
                    </Typography>
                    {(() => {
                      const chart = buildProjectionChart(projection.attended, "#03B4C6");
                      return (
                        <MultiLineChart
                          labels={chart.labels}
                          series={chart.series}
                          band={chart.band}
                          showValues
                        />
                      );
                    })()}
                  </Box>
                  <Box sx={{ ...sectionStyle, flex: "1 1 420px" }}>
                    <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 0.5 }}>
                      Projeção — faturamento líquido (6 meses)
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "#6c757d", mb: 2 }}>
                      Modelo: {projection.revenue.model}
                      {projection.revenue.mape != null && ` · erro médio (MAPE) ${projection.revenue.mape}%`}
                      {" · banda ~80% de confiança"}
                    </Typography>
                    {(() => {
                      const chart = buildProjectionChart(projection.revenue, "#f39c12");
                      return (
                        <MultiLineChart
                          labels={chart.labels}
                          series={chart.series}
                          band={chart.band}
                          valueFormatter={formatCurrency}
                          showValues
                          labelFormatter={formatCurrencyCompact}
                        />
                      );
                    })()}
                  </Box>
                </Box>
              )}

              {projection && projection.warnings.length > 0 && (
                <Box sx={{ ...sectionStyle, py: 1.5 }}>
                  {projection.warnings.map((warning) => (
                    <Typography key={warning} sx={{ fontSize: 12, color: "#6c757d" }}>
                      • {warning}
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          )}
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default ReportEvolution;
